import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, ScanLine, X } from 'lucide-react';

/**
 * Native-feel scanner input.
 *
 * Three input paths from one component:
 *
 *   1. Hardware keyboard-wedge scanner → just types into the input
 *      and presses Enter; form catches the Enter and submits.
 *
 *   2. On-screen typing → same Input, operator can tap-and-type.
 *
 *   3. Camera scanning, two engines under one button:
 *        a. Native `window.BarcodeDetector` API (Chromium browsers
 *           on Android / desktop). Zero JS download.
 *        b. `@zxing/browser` JS fallback for iOS Safari (no native
 *           BarcodeDetector — Apple hasn't shipped it). Loaded
 *           lazily the first time the operator taps the camera
 *           icon so the JS isn't on the critical path for everyone.
 *
 * Camera button is now ALWAYS shown on touch devices — even when
 * neither engine has loaded yet — and we load the fallback engine
 * on demand if the native one is missing.
 */

interface BarcodeDetectorLike {
  detect(image: ImageBitmapSource): Promise<Array<{ rawValue: string }>>;
}
interface BarcodeDetectorCtor {
  new (init?: { formats?: string[] }): BarcodeDetectorLike;
}

function getNativeBarcodeDetectorCtor(): BarcodeDetectorCtor | null {
  if (typeof window === 'undefined') return null;
  const ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor })
    .BarcodeDetector;
  return ctor ?? null;
}

function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    (navigator.maxTouchPoints ?? 0) > 0 ||
    window.matchMedia?.('(hover: none)').matches === true
  );
}

export interface BarcodeScannerProps {
  onSubmit: (code: string) => void | Promise<void>;
  placeholder?: string;
  label?: string;
  clearOnSubmit?: boolean;
  disabled?: boolean;
  enableCamera?: boolean;
}

export function BarcodeScanner({
  onSubmit,
  placeholder = 'Scan or type code…',
  label,
  clearOnSubmit = true,
  disabled = false,
  enableCamera = true,
}: BarcodeScannerProps) {
  const [draft, setDraft] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Always offer a camera button on touch devices. Desktop with no
  // BarcodeDetector (rare) hides it — those callers use the keyboard
  // input or a USB scanner anyway.
  const cameraSupported =
    enableCamera && (!!getNativeBarcodeDetectorCtor() || isTouchDevice());

  const submit = useCallback(
    async (raw: string) => {
      const code = raw.trim();
      if (!code || disabled) return;
      await onSubmit(code);
      if (clearOnSubmit) {
        setDraft('');
        inputRef.current?.focus();
      }
    },
    [onSubmit, clearOnSubmit, disabled],
  );

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-[11.5px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </label>
      )}
      <form
        className="flex items-stretch gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit(draft);
        }}
      >
        <div className="relative flex-1">
          <ScanLine className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className="block h-14 w-full rounded-2xl border border-border bg-card pl-10 pr-3 font-mono text-[15px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>
        {cameraSupported && (
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            disabled={disabled}
            aria-label="Open camera"
            className="pwa-tap inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm active:opacity-90 disabled:opacity-40"
          >
            <Camera className="h-5 w-5" />
          </button>
        )}
        <button
          type="submit"
          disabled={disabled || !draft.trim()}
          className="pwa-tap inline-flex h-14 shrink-0 items-center justify-center rounded-2xl border border-border bg-card px-4 text-[14px] font-semibold text-foreground active:bg-muted disabled:opacity-40"
        >
          Enter
        </button>
      </form>

      {cameraOpen &&
        createPortal(
          <CameraScanner
            onDetected={(code) => {
              setCameraOpen(false);
              void submit(code);
            }}
            onClose={() => setCameraOpen(false)}
          />,
          document.body,
        )}
    </div>
  );
}

// ─────────────────────────── CameraScanner ────────────────────────────

/**
 * Full-screen camera overlay. Picks the native BarcodeDetector engine
 * when it exists; otherwise lazy-loads @zxing/browser for iOS Safari.
 */
function CameraScanner({
  onDetected,
  onClose,
}: {
  onDetected: (code: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [engineHint, setEngineHint] = useState<string>('Initializing camera…');

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;
    let nativeTimer: ReturnType<typeof setInterval> | null = null;
    let zxingControls: { stop: () => void } | null = null;

    const stopAll = () => {
      cancelled = true;
      if (nativeTimer) clearInterval(nativeTimer);
      if (zxingControls) zxingControls.stop();
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Camera not available on this device.');
          return;
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();

        const NativeCtor = getNativeBarcodeDetectorCtor();
        if (NativeCtor) {
          setEngineHint('Looking for code…');
          const detector = new NativeCtor({
            formats: [
              'qr_code',
              'code_128',
              'ean_13',
              'ean_8',
              'code_39',
              'upc_a',
              'upc_e',
              'itf',
            ],
          });
          nativeTimer = setInterval(async () => {
            if (cancelled || !video.videoWidth) return;
            try {
              const codes = await detector.detect(video);
              if (codes.length > 0) {
                const code = codes[0].rawValue?.trim();
                if (code) onDetected(code);
              }
            } catch {
              /* transient frame errors are fine */
            }
          }, 100);
          return;
        }

        // iOS Safari path — lazy-load zxing.
        setEngineHint('Loading scanner…');
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader();
        setEngineHint('Looking for code…');
        zxingControls = await reader.decodeFromVideoElement(
          video,
          (result) => {
            if (cancelled) return;
            const text = result?.getText().trim();
            if (text) onDetected(text);
          },
        );
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Camera access denied.';
        setError(msg);
      }
    })();

    return stopAll;
  }, [onDetected]);

  return (
    <div className="pwa-screen pwa-pt-safe pwa-pb-safe fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-[14px] font-semibold">Camera scan</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close camera"
          className="pwa-tap rounded-full bg-white/15 p-2 active:bg-white/25"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="relative flex-1">
        <video
          ref={videoRef}
          playsInline
          muted
          className="h-full w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-44 w-72 max-w-[80vw] rounded-2xl border-2 border-primary shadow-[0_0_0_99999px_rgba(0,0,0,0.55)]" />
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 rounded-xl bg-black/80 px-4 py-2 text-[12px] text-white">
          {error ?? engineHint}
        </div>
      </div>
    </div>
  );
}
